import { useCallback, useEffect, useRef, useState } from "react";

type SetValue<T> = (value: T | ((prev: T) => T)) => void;

/**
 * Synced state with `localStorage`. Returns `[value, setValue, removeValue]`.
 * SSR-safe: returns `initialValue` during the first render, then hydrates from
 * storage on mount. Listens for `storage` events so the value stays in sync
 * across tabs.
 *
 *   const [theme, setTheme] = useLocalStorage<"light" | "dark">("theme", "light");
 *
 * Adapted from usehooks-ts (MIT). The `initialValue` reference is captured on
 * first render so passing an inline object/array doesn't cause re-renders.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, SetValue<T>, () => void] {
  // Capture initialValue reference so inline objects don't churn dependencies.
  const initialRef = useRef(initialValue);

  const readValue = useCallback((): T => {
    if (typeof window === "undefined") return initialRef.current;
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initialRef.current : (JSON.parse(raw) as T);
    } catch {
      return initialRef.current;
    }
  }, [key]);

  const [value, setStoredValue] = useState<T>(initialValue);

  useEffect(() => {
    setStoredValue(readValue());
    const onStorage = (e: StorageEvent) => {
      if (e.key === key) setStoredValue(readValue());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, readValue]);

  const setValue: SetValue<T> = useCallback(
    (next) => {
      setStoredValue((prev) => {
        const resolved =
          typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* quota / private mode — ignore */
        }
        return resolved;
      });
    },
    [key],
  );

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setStoredValue(initialRef.current);
  }, [key]);

  return [value, setValue, removeValue];
}
